import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { BookingDetailTab } from './bookingDetailTabModel';
import type { OverviewTodo } from './overviewTodos';

const button = (urgent: boolean) =>
  cn(
    'rounded-pill flex h-11 items-center px-3.5 text-xs font-bold',
    urgent ? 'bg-forest text-white' : 'bg-card-alt text-ink',
  );

/** „Zu erledigen“ (Board „Buchung“): nummeriert, Bernstein wartet auf jemanden, der Knopf führt hin. */
export function OverviewTodoList({
  todos,
  unknown,
  onChangeTab,
}: {
  todos: OverviewTodo[];
  /** Quellen ohne Stand; solange eine fehlt, gibt es kein „Gerade nichts offen“. */
  unknown: string[];
  onChangeTab: (tab: BookingDetailTab) => void;
}) {
  return (
    <section
      aria-labelledby="todo-title"
      className="rounded-tile shadow-float flex flex-col gap-3 bg-white p-5"
    >
      <h2
        id="todo-title"
        className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
      >
        Zu erledigen
      </h2>
      {todos.length === 0 && unknown.length === 0 && (
        <p className="flex items-center gap-2 text-sm">
          <Check size={16} aria-hidden className="text-forest" /> Gerade nichts offen.
        </p>
      )}
      {unknown.length > 0 && (
        <p className="text-ink-soft text-xs">Noch nicht vollständig: {unknown.join(' · ')}.</p>
      )}
      {todos.length > 0 && (
        <ol className="flex flex-col gap-2">
          {todos.map(({ go, ...todo }, index) => (
            <li
              key={todo.title}
              className="bg-paper rounded-control flex flex-wrap items-center gap-3 p-3"
            >
              <span
                aria-hidden
                className={cn(
                  'rounded-pill flex size-7 flex-none items-center justify-center text-xs font-extrabold',
                  todo.urgent ? 'bg-amber-tint text-amber-ink' : 'bg-forest-soft text-forest-deep',
                )}
              >
                {index + 1}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <b className="text-sm">{todo.title}</b>
                <span className="text-ink-soft text-xs">{todo.sub}</span>
              </span>
              {go && 'tab' in go && (
                <button
                  type="button"
                  onClick={() => onChangeTab(go.tab)}
                  className={button(todo.urgent)}
                >
                  {go.label}
                </button>
              )}
              {go && 'propertyId' in go && (
                <Link
                  to="/properties/$id"
                  params={{ id: go.propertyId }}
                  className={button(todo.urgent)}
                >
                  {go.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
